import { computed, inject, Injectable, signal } from '@angular/core';
import { areIntervalsOverlapping, formatISO, startOfDay, subDays } from 'date-fns';
import { catchError, Observable, of, throwError } from 'rxjs';
import { UserService } from '@ipg/ngx-frontend-toolset';
import { DialogService } from '../../../components/dialog/dialog.service';
import { ClientEditor } from '../../../models/clients.model';
import { User } from '../../../models/user.model';
import { ClientEditorService } from './client-editor.service';

interface BlockedRange {
  start: Date;
  end: Date;
  editorName: string;
  editorUserId: string;
}

@Injectable({
  providedIn: 'root',
})
export class RangeClaimManagerService {
  private readonly _clientEditorService = inject(ClientEditorService);
  private readonly _dialog = inject(DialogService);
  private readonly _userService = inject<UserService<User>>(UserService);

  // State
  readonly clientEditors = signal<ClientEditor[]>([]);

  readonly currentUserId = signal<string | null>(this._userService.data()?._id ?? null);

  readonly renderedClientEditors = computed(() =>
    this.clientEditors()
      .map((editor) => ({
        ...editor,
        ranges: editor.ranges.map((range) => {
          const toDateObj = new Date(range.toDate);
          if (isNaN(toDateObj.getTime())) {
            console.error(
              `Invalid toDate for editor ${editor.name} (${editor.userId}): ${range.toDate}`
            );
            return range;
          }
          return {
            ...range,
            toDate: formatISO(subDays(toDateObj, 1)),
          };
        }),
      }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  );

  readonly currentClientEditor = computed(() => {
    const editors = this.clientEditors();
    const userId = this.currentUserId();

    return editors.find((editor) => editor.userId === userId) ?? null;
  });

  readonly blockedRanges = computed(() => {
    const editors = this.clientEditors();
    const currentEditor = this.currentClientEditor();

    if (!currentEditor) {
      // No claim → all ranges from others are blocked
      return editors.flatMap((editor) =>
        editor.ranges.map((range) => ({
          start: startOfDay(new Date(range.fromDate)),
          end: startOfDay(new Date(range.toDate)),
          editorName: editor.name,
          editorUserId: editor.userId,
        }))
      );
    }

    // Has claim → only ranges from OTHERS are blocked
    return editors
      .filter((editor) => editor.userId !== currentEditor.userId)
      .flatMap((editor) =>
        editor.ranges.map((range) => ({
          start: startOfDay(new Date(range.fromDate)),
          end: startOfDay(new Date(range.toDate)),
          editorName: editor.name,
          editorUserId: editor.userId,
        }))
      );
  });

  readonly hasBlockedRanges = computed(() => {
    return this.blockedRanges().length > 0;
  });

  // Operations
  claimRange(clientId: string, range: { start: Date; end: Date }): Observable<unknown> {
    return this._clientEditorService
      .claimRange(clientId, {
        fromDate: range.start,
        toDate: range.end,
      })
      .pipe(
        catchError((error) => {
          this._dialog.alert({
            title: 'Foutmelding',
            message: 'Er is iets misgegaan bij het claimen. Probeer het opnieuw.',
            confirmText: 'OK',
          });

          return throwError(() => error);
        })
      );
  }

  refreshClaimInBackground(
    clientId: string,
    range: { start: Date; end: Date }
  ): Observable<unknown> {
    // Re-claim current visible range
    // Backend will automatically fill gaps
    return this._clientEditorService
      .claimRange(clientId, {
        fromDate: range.start,
        toDate: range.end,
      })
      .pipe(
        catchError((error) => {
          // Silent fail - don't show error to user
          console.warn('Failed to refresh claim in background', error);
          return of(undefined);
        })
      );
  }

  revokeAllClaims(clientId: string): Observable<unknown> {
    return this._clientEditorService.revoke(clientId);
  }

  updateActivity(clientId: string): Observable<unknown> {
    return this._clientEditorService.updateActivity(clientId);
  }

  // Validation
  isDateInBlockedRange(date: Date): boolean {
    return this.blockedRanges().some(
      (range) => date >= range.start && date <= range.end
    );
  }

  isIntervalInBlockedRange(start: Date, end: Date): boolean {
    return this.blockedRanges().some((blockedRange) => {
      const hasOverlap = areIntervalsOverlapping(
        { start, end },
        { start: blockedRange.start, end: blockedRange.end },
        { inclusive: false }
      );

      return hasOverlap;
    });
  }

  getBlockedRangeForInterval(start: Date, end: Date): BlockedRange | null {
    return (
      this.blockedRanges().find((range) =>
        areIntervalsOverlapping(
          { start, end },
          { start: range.start, end: range.end },
          { inclusive: true }
        )
      ) ?? null
    );
  }

  // Editor Management
  setEditors(editors: ClientEditor[]): void {
    this.clientEditors.set(editors);
  }

  getCurrentEditorRanges(): { fromDate: string; toDate: string }[] {
    const currentEditor = this.currentClientEditor();
    return currentEditor?.ranges ?? [];
  }

  // Auto-refresh Detection
  shouldTriggerAutoRefresh(
    prev: ClientEditor[],
    curr: ClientEditor[],
    currentUserId: string
  ): boolean {
    // Check if someone else was revoked (editor count decreased)
    const someoneRevoked = curr.length < prev.length;

    // Check if any OTHER editor's ranges changed
    const otherEditorsRangesChanged = curr.some((currEditor) => {
      // Skip our own editor
      if (currEditor.userId === currentUserId) {
        return false;
      }

      // Find matching editor in previous state
      const prevEditor = prev.find((e) => e.userId === currEditor.userId);
      if (!prevEditor) {
        return false; // New editor (not a range change)
      }

      // Compare ranges
      return JSON.stringify(currEditor.ranges) !== JSON.stringify(prevEditor.ranges);
    });

    // Trigger auto-refresh if:
    // 1. Someone else was revoked (editor count decreased), OR
    // 2. Another editor's ranges changed (might have freed up space)
    return someoneRevoked || otherEditorsRangesChanged;
  }
}
